import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Layers, Sliders, RefreshCw, Wallet, PieChart, TrendingDown } from "lucide-react";
import { PortfolioDonut } from "./portfolio/PortfolioDonut";
import { TrimAnalysis } from "./portfolio/TrimAnalysis";
import { CashDeploy } from "./portfolio/CashDeploy";
import { LimitsBar } from "./portfolio/LimitsBar";
import { DualScore } from "./portfolio/DualScore";
import { useDualScores } from "./portfolio/useDualScores";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem("apex_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const HOLD_COLORS = {
  "STRONG HOLD": "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  "HOLD": "text-cyan-400 border-cyan-500/40 bg-cyan-500/10",
  "HOLD / NO ADD": "text-amber-400 border-amber-500/40 bg-amber-500/10",
  "TRIM (couper 30-50%)": "text-orange-400 border-orange-500/40 bg-orange-500/10",
  "EXIT (thèse cassée)": "text-red-400 border-red-500/40 bg-red-500/10",
};

const fmt = (n, dp = 0) => (n === null || n === undefined) ? "—" :
  new Intl.NumberFormat("fr-FR", { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(n);

export default function PortfolioEngine({ owner = "Helmi" }) {
  const [consolidated, setConsolidated] = useState(null);
  const [exposure, setExposure] = useState(null);
  const [holdScores, setHoldScores] = useState({});
  const [loading, setLoading] = useState(false);
  const [simTicker, setSimTicker] = useState("");
  const [simAction, setSimAction] = useState("reduce");
  const [simAmount, setSimAmount] = useState("");
  const [simTargetPct, setSimTargetPct] = useState("");
  const [simResult, setSimResult] = useState(null);
  const [simBusy, setSimBusy] = useState(false);
  const [settings, setSettings] = useState({ cap_pct: 15, ai_limit_pct: 40 });
  const CAP_PCT = settings.cap_pct;
  const { scores: dual, pending: dualPending } = useDualScores((consolidated?.consolidated_by_ticker || []).map((r) => r.ticker));

  useEffect(() => {
    axios.get(`${API}/portfolio-engine/settings/${owner}`, { headers: authHeaders() }).then((r) => setSettings(r.data)).catch(() => {});
  }, [owner]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cRes, eRes, hRes] = await Promise.all([
        axios.get(`${API}/portfolio-engine/consolidated/${owner}`, { headers: authHeaders() }),
        axios.get(`${API}/portfolio-engine/exposure/${owner}`, { headers: authHeaders() }),
        axios.post(`${API}/portfolio-engine/hold-score/${owner}`, {}, { headers: authHeaders() }).catch(() => ({ data: null })),
      ]);
      setConsolidated(cRes.data);
      setExposure(eRes.data);
      if (hRes.data && hRes.data.results) {
        const map = {};
        hRes.data.results.forEach(r => { map[r.ticker] = r; });
        setHoldScores(map);
      }
    } catch (e) {
      console.error("Portfolio Engine fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [owner]);

  const runSimulation = async () => {
    if (!simTicker) return;
    setSimBusy(true); setSimResult(null);
    try {
      const body = { ticker: simTicker.toUpperCase() };
      if (simAction === "reduce") {
        if (simTargetPct) body.target_weight_pct = parseFloat(simTargetPct);
        else if (simAmount) body.reduce_amount = parseFloat(simAmount);
      } else {
        // Reinforce = negative reduction with sign flip. We simulate by computing
        // the new weight manually.
        const tk = consolidated?.consolidated_by_ticker?.find(r => r.ticker === simTicker.toUpperCase());
        if (tk) {
          const buy = parseFloat(simAmount || 0);
          const total = consolidated.portfolio_total_value;
          const newVal = tk.total_value + buy;
          const newWeight = (newVal / total) * 100;
          setSimResult({
            feasible: true,
            simulated_reinforce: true,
            ticker: tk.ticker,
            before_value: tk.total_value,
            before_weight_pct: tk.weight_pct,
            buy_value: buy,
            after_value: newVal,
            after_weight_pct: parseFloat(newWeight.toFixed(2)),
            weight_delta_pp: parseFloat((newWeight - tk.weight_pct).toFixed(2)),
            portfolio_total_value: total,
            explanation: `Achat de ${fmt(buy)}€ → ${tk.ticker} passe de ${tk.weight_pct}% à ${newWeight.toFixed(2)}%`,
          });
          setSimBusy(false); return;
        }
      }
      const r = await axios.post(`${API}/portfolio-engine/reduce-simulate/${owner}`, body, { headers: authHeaders() });
      setSimResult(r.data);
    } catch (e) {
      setSimResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setSimBusy(false);
    }
  };

  const brokerBreakdown = useMemo(() => {
    if (!consolidated?.consolidated_by_ticker) return {};
    const brokers = {};
    consolidated.consolidated_by_ticker.forEach(r => {
      (r.brokers || []).forEach(b => {
        if (!brokers[b.broker]) brokers[b.broker] = { value: 0, tickers: 0 };
        brokers[b.broker].value += b.value || 0;
        brokers[b.broker].tickers += 1;
      });
    });
    return brokers;
  }, [consolidated]);

  const duplicates = useMemo(() =>
    consolidated?.consolidated_by_ticker?.filter(r => r.brokers_count > 1) || [],
    [consolidated]);

  if (loading && !consolidated) {
    return <div className="p-6 text-slate-400" data-testid="pe-loading">Chargement Portfolio Engine…</div>;
  }
  if (!consolidated) {
    return <div className="p-6 text-slate-400">Aucune donnée</div>;
  }

  return (
    <div className="p-3 sm:p-6 space-y-6" data-testid="portfolio-engine-tab">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            <Layers className="w-6 h-6 text-cyan-400" /> Portfolio Engine
          </h1>
          <p className="text-sm text-slate-400">
            Consolidation multi-broker · Entry / Hold / Risk indépendants · Simulateur de réduction / renforcement
          </p>
        </div>
        <button onClick={fetchAll} disabled={loading}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm hover:bg-slate-700 flex items-center gap-2"
          data-testid="pe-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
        </button>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
          <div className="text-xs text-slate-400">Total Portfolio</div>
          <div className="text-xl font-bold text-cyan-400">€{fmt(consolidated.portfolio_total_value)}</div>
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
          <div className="text-xs text-slate-400">Positions consolidées</div>
          <div className="text-xl font-bold text-slate-100">{consolidated.positions_count}</div>
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
          <div className="text-xs text-slate-400">Doublons inter-brokers</div>
          <div className="text-xl font-bold text-amber-400">{duplicates.length}</div>
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
          <div className="text-xs text-slate-400">Brokers actifs</div>
          <div className="text-xl font-bold text-slate-100">{Object.keys(brokerBreakdown).length}</div>
        </div>
      </div>

      {/* BROKER BREAKDOWN */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pe-broker-breakdown">
        {Object.entries(brokerBreakdown).map(([b, v]) => (
          <div key={b} className="bg-slate-800/30 border border-slate-700 rounded p-2">
            <div className="text-xs text-slate-400">{b}</div>
            <div className="text-sm font-bold text-slate-100">€{fmt(v.value)}</div>
            <div className="text-[10px] text-slate-500">{v.tickers} tickers · {(v.value / consolidated.portfolio_total_value * 100).toFixed(1)}%</div>
          </div>
        ))}
      </div>

      <LimitsBar owner={owner} settings={settings} onChange={setSettings} />

      <PortfolioDonut rows={consolidated.consolidated_by_ticker} total={consolidated.portfolio_total_value} cap={CAP_PCT} />

      {/* CONSOLIDATED TABLE */}
      <div className="bg-slate-800/30 border border-slate-700 rounded-lg overflow-hidden" data-testid="pe-consolidated">
        <div className="px-3 py-2 border-b border-slate-700 flex items-center justify-between">
          <div className="font-semibold text-slate-100 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-cyan-400" /> Positions consolidées par ticker
          </div>
          <div className="text-xs text-slate-500">cross-brokers · poids réel</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-800/60">
              <tr className="text-left text-slate-400">
                <th className="px-2 py-2">Ticker</th>
                <th className="px-2 py-2 text-right">Valeur €</th>
                <th className="px-2 py-2 text-right">Poids %</th>
                <th className="px-2 py-2 text-right">PnL %</th>
                <th className="px-2 py-2" title="G = potentiel 5-10 ans · I = attractivité au prix actuel">Growth / Invest.</th>
                <th className="px-2 py-2">Brokers</th>
                <th className="px-2 py-2 text-right">Hold /100</th>
                <th className="px-2 py-2">Verdict Hold</th>
                <th className="px-2 py-2">Secteur</th>
              </tr>
            </thead>
            <tbody>
              {consolidated.consolidated_by_ticker.map(r => {
                const h = holdScores[r.ticker];
                return (
                  <tr key={r.ticker} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`pe-row-${r.ticker}`}>
                    <td className="px-2 py-2 font-bold text-slate-100">{r.ticker}</td>
                    <td className="px-2 py-2 text-right font-mono">€{fmt(r.total_value)}</td>
                    <td className={`px-2 py-2 text-right font-mono ${r.weight_pct > CAP_PCT ? "text-red-400 font-bold" : r.weight_pct > 10 ? "text-amber-400" : "text-slate-300"}`}>
                      {r.weight_pct.toFixed(2)}%
                    </td>
                    <td className={`px-2 py-2 text-right font-mono ${r.pnl_pct > 0 ? "text-emerald-400" : "text-red-400"}`} data-testid={`pe-pnl-${r.ticker}`}>
                      {r.pnl_pct !== null && r.pnl_pct !== undefined ? `${r.pnl_pct > 0 ? "+" : ""}${r.pnl_pct.toFixed(1)}%` : "—"}
                    </td>
                    <td className="px-2 py-2">
                      <DualScore s={dual[r.ticker]} loading={dualPending.includes(r.ticker)} ticker={r.ticker} />
                    </td>
                    <td className="px-2 py-2 text-slate-400">
                      {r.brokers_count > 1
                        ? <span className="text-amber-400 font-semibold">×{r.brokers_count} {(r.brokers || []).map(b => b.broker.slice(0, 3)).join("+")}</span>
                        : ((r.brokers || [])[0]?.broker || "—")}
                    </td>
                    <td className="px-2 py-2 text-right font-mono font-bold">{h?.hold_total ?? "—"}</td>
                    <td className="px-2 py-2">
                      {h?.verdict ? (
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${HOLD_COLORS[h.verdict] || ""}`}>
                          {h.verdict}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-2 py-2 text-slate-500">{r.sector || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <TrimAnalysis owner={owner} rows={consolidated.consolidated_by_ticker} total={consolidated.portfolio_total_value} exposure={exposure} cap={CAP_PCT} aiLimit={settings.ai_limit_pct} />

      <CashDeploy owner={owner} />

      {/* EXPOSURE — sector + theme */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-3" data-testid="pe-sectors">
          <div className="font-semibold text-slate-100 mb-2 flex items-center gap-2">
            <PieChart className="w-4 h-4 text-cyan-400" /> Exposition par secteur
          </div>
          {Object.entries(exposure?.by_sector || {})
            .sort((a, b) => b[1].pct - a[1].pct).slice(0, 10)
            .map(([s, v]) => (
              <div key={s} className="flex items-center gap-2 mb-1 text-xs">
                <div className="w-32 text-slate-400 truncate">{s}</div>
                <div className="flex-1 bg-slate-700 rounded-full h-2 overflow-hidden">
                  <div className={`h-full ${v.pct > 40 ? "bg-red-500" : v.pct > 30 ? "bg-amber-500" : "bg-cyan-500"}`}
                    style={{ width: `${Math.min(100, v.pct)}%` }} />
                </div>
                <div className="w-16 text-right font-mono text-slate-200">{v.pct.toFixed(1)}%</div>
              </div>
            ))}
        </div>
        <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-3" data-testid="pe-themes">
          <div className="font-semibold text-slate-100 mb-2 flex items-center gap-2">
            <PieChart className="w-4 h-4 text-purple-400" /> Exposition par thème
          </div>
          {Object.entries(exposure?.by_theme || {})
            .sort((a, b) => b[1].pct - a[1].pct).slice(0, 10)
            .map(([t, v]) => (
              <div key={t} className="flex items-center gap-2 mb-1 text-xs">
                <div className="w-32 text-slate-400 truncate">{t}</div>
                <div className="flex-1 bg-slate-700 rounded-full h-2 overflow-hidden">
                  <div className={`h-full ${v.pct > 40 ? "bg-red-500" : v.pct > 30 ? "bg-amber-500" : "bg-purple-500"}`}
                    style={{ width: `${Math.min(100, v.pct)}%` }} />
                </div>
                <div className="w-16 text-right font-mono text-slate-200">{v.pct.toFixed(1)}%</div>
              </div>
            ))}
        </div>
      </div>

      {/* SIMULATOR */}
      <div className="bg-slate-800/50 border border-cyan-500/30 rounded-lg p-4" data-testid="pe-simulator">
        <div className="font-semibold text-slate-100 mb-3 flex items-center gap-2">
          <Sliders className="w-5 h-5 text-cyan-400" /> Simulateur de réduction / renforcement
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-3">
          <select value={simTicker} onChange={e => setSimTicker(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm"
            data-testid="pe-sim-ticker">
            <option value="">Choisir ticker…</option>
            {consolidated.consolidated_by_ticker.map(r =>
              <option key={r.ticker} value={r.ticker}>{r.ticker} ({r.weight_pct.toFixed(1)}%)</option>)}
          </select>
          <select value={simAction} onChange={e => setSimAction(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm"
            data-testid="pe-sim-action">
            <option value="reduce">Réduire</option>
            <option value="reinforce">Renforcer</option>
          </select>
          <input type="number" placeholder="Montant €" value={simAmount}
            onChange={e => { setSimAmount(e.target.value); setSimTargetPct(""); }}
            className="bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm"
            data-testid="pe-sim-amount" />
          {simAction === "reduce" && (
            <input type="number" placeholder="OU cible % PF" value={simTargetPct} step="0.1"
              onChange={e => { setSimTargetPct(e.target.value); setSimAmount(""); }}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm"
              data-testid="pe-sim-target" />
          )}
          <button onClick={runSimulation} disabled={simBusy || !simTicker}
            className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 text-white rounded px-3 py-2 text-sm font-semibold"
            data-testid="pe-sim-run">
            {simBusy ? "Calcul…" : "Simuler"}
          </button>
        </div>

        {simResult && (
          <div className="mt-3 border-t border-slate-700 pt-3" data-testid="pe-sim-result">
            {simResult.error && <div className="text-red-400 text-sm">{simResult.error}</div>}
            {simResult.feasible === false && (
              <div className="text-amber-400 text-sm">
                <TrendingDown className="w-4 h-4 inline mr-1" /> {simResult.reason}
              </div>
            )}
            {simResult.feasible === true && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="bg-slate-900/60 border border-slate-700 rounded p-2">
                  <div className="text-slate-400">Avant</div>
                  <div className="text-lg font-bold text-slate-100">€{fmt(simResult.before_value)}</div>
                  <div className="text-cyan-400">{simResult.before_weight_pct?.toFixed(2)}% du PF</div>
                </div>
                <div className="bg-slate-900/60 border border-slate-700 rounded p-2">
                  <div className="text-slate-400">Opération</div>
                  <div className="text-lg font-bold text-amber-400">
                    {simResult.simulated_reinforce ? "+" : "-"}€{fmt(simResult.simulated_reinforce ? simResult.buy_value : simResult.sale_value)}
                  </div>
                </div>
                <div className="bg-slate-900/60 border border-slate-700 rounded p-2">
                  <div className="text-slate-400">Après</div>
                  <div className="text-lg font-bold text-slate-100">€{fmt(simResult.after_value)}</div>
                  <div className="text-emerald-400">{simResult.after_weight_pct?.toFixed(2)}% du PF</div>
                </div>
                <div className="bg-slate-900/60 border border-slate-700 rounded p-2">
                  <div className="text-slate-400">Δ Poids</div>
                  <div className={`text-lg font-bold ${(simResult.weight_reduction_pp || simResult.weight_delta_pp || 0) > 0 ? "text-red-400" : "text-emerald-400"}`}>
                    {(simResult.weight_reduction_pp !== undefined ? -simResult.weight_reduction_pp : simResult.weight_delta_pp)?.toFixed(2)} pp
                  </div>
                </div>
              </div>
            )}
            {simResult.explanation && (
              <div className="mt-2 text-xs text-slate-400 italic">{simResult.explanation}</div>
            )}
          </div>
        )}
        <div className="mt-2 text-[10px] text-slate-500">
          Note : le renforcement suppose que la valeur totale du portefeuille croît du montant investi (cash → equity).
          La réduction suppose conservation de valeur (equity → cash).
        </div>
      </div>

    </div>
  );
}
